import React, { useEffect, useState } from 'react';

import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Web, Home } from '@mui/icons-material';
import { Radio, Tooltip } from '@mui/material';
import { Row, Col, Form, Dropdown } from 'react-bootstrap';
import Button from '../../../components/Button';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setClientData } from './addClientUserSlice';
import { createNewUser, getClient, getAllUsers, getAMCClients, createUserCustomer } from './operations';
import styles from './styles';
import { SubHeader, InputBox, ModalConfirmation, Footer } from '../../../components';
import useIsMobileScreen from '../../../hooks/useCheckIsMobile';

import './styles.scoped.css';

const chunkAllSettled = async (items, asyncFn, batchSize = 10) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map(item => asyncFn(item)));
    results.push(...settled);
  }
  return results;
};

const AddClientUser = () => {
  const dispatch = useDispatch();
  const navigation = useNavigate();
  const clientData = useSelector(state => state.addClientUser.clientData);
  const [clientId, setClientId] = useState();
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [nullResult, setNullResult] = useState(false);
  const [selectedContact, setSelectedContact] = useState();
  const [selectedClient, setSelectedClient] = useState('Single Client');
  const [errors, setErrors] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const isMobile = useIsMobileScreen();

  useEffect(() => {
    getAllUsers();
    dispatch(setClientData({}));
    setSelectedClient('Single Client');
  }, []);

  useEffect(() => {
    if (clientId === '') {
      dispatch(setClientData({}));
      setSelectedContact(null);
      setSelectedClient('Single Client');
    }
  }, [clientId]);

  const handleSelectClientToggle = (value) => {
    setSelectedClient((prev) => (prev === value ? 'Single Client' : value));
  }

  const fetchClient = async () => {
    if (!clientId || clientId === '') return;
    setLoading(true);
    setNullResult(false);
    setSelectedContact(null);
    setSelectedClient('Single Client');
    setErrors([]);
    const response = await getClient(clientId);
    if (response.response_code === 200) {
      const status = response.response_body?.clientStatus?.split(' ')[0];
      const statuses = process.env.REACT_APP_CLIENT_STATUSES.split(',');
      if (statuses.includes(status)) {
        dispatch(setClientData(response.response_body));
      } else {
        setTitle('Invalid');
        setMessage('Client not active in Ultra Staff');
      }
    } else {
      setNullResult(true);
      setClientId(null);
      setSelectedContact(null);
      setSelectedClient('Single Client');
    }
    setLoading(false);
  };

  // CP-786: fetch every client under the AMC and create a UserCustomer row per client for the new
  // approver. Runs in real time on add so the approver is never left with zero clients until the
  // nightly reconcile. Returns { total, failed } so the caller can message a partial attach; a
  // thrown error (e.g. AMC endpoint unavailable) is treated as "all failed" and left to the nightly
  // job rather than failing the user creation that already succeeded.
  const attachAmcClients = async (userId, amc) => {
    try {
      const amcRes = await getAMCClients(amc);
      const amcClients = Array.isArray(amcRes?.response_body) ? amcRes.response_body : [];
      // Dedupe by CUST_ID — the proc joins bill-to rows, so a client can appear more than once.
      const uniqueCustIds = [...new Set(amcClients.map(c => String(c?.CUST_ID ?? '').trim()).filter(Boolean))];
      if (uniqueCustIds.length === 0) return { total: 0, failed: 0 };

      const results = await chunkAllSettled(uniqueCustIds, custId =>
        createUserCustomer({ active: true, customerId: custId, userCustomersId: userId }),
      );
      const failed = results.filter(r => r.status === 'rejected' || r.value === false).length;
      return { total: uniqueCustIds.length, failed };
    } catch (e) {
      return { total: 0, failed: 1 };
    }
  };

  const add = async () => {
    if (adding) return;
    if (!selectedContact?.firstName || !selectedContact?.lastName || !selectedContact?.emailAddress) {
      setTitle('Invalid');
      setMessage('Cannot add until issues are corrected in Ultra Staff');
      return;
    }
    setAdding(true);
    const startsWithOne = selectedContact?.cellPhone?.substring(0, 1) === '1';
    const contactStatus = selectedContact.status?.split(' ')[0];
    const contactStatuses = process.env.REACT_APP_CONTACT_STATUSES.split(',');

    if (contactStatuses.includes(contactStatus)) {
      try {
        const isAmcMode = selectedClient !== 'Single Client';
        const amcCode = clientData?.clientAccountMasterCode?.trim();

        const response = await createNewUser({
          id: selectedContact?.emailAddress,
          name: `${selectedContact?.firstName} ${selectedContact?.lastName}`,
          customerId: clientId,
          status: 'Invited',
          type: 'Client',
          lastLogin: new Date().toISOString(),
          clientName: clientData.clientName,
          cellPhone:
            selectedContact?.smsOptIn === true && selectedContact?.cellPhone
              ? `+${startsWithOne ? '' : '1'}${selectedContact?.cellPhone?.replace(/\D/g, '')}`
              : '',
          customersAddedByAmc: isAmcMode ? amcCode : null,
        });

        if (response.errors?.length) {
          alert(response.errors[0].message ? 'User already exist' : null);
        } else if (isAmcMode && amcCode) {
          // CP-786: attach the AMC's clients in real time so a freshly-added approver has their
          // client list immediately, instead of waiting for the next overnight run. The nightly
          // job remains a safety net — it reconciles anything missed here (partial-attach failures
          // or clients added under the AMC after this point).
          const attachResult = await attachAmcClients(selectedContact.emailAddress, amcCode);
          if (attachResult.failed > 0) {
            setTitle('Partial Success');
            setMessage(
              attachResult.total > 0
                ? `The user was added, but ${attachResult.failed} of ${attachResult.total} AMC clients ` +
                    'could not be attached. The overnight sync will attach any missing clients.'
                : 'The user was added, but the AMC client list could not be retrieved. ' +
                    'The overnight sync will attach the clients.',
            );
          } else {
            setTitle('Success');
            setMessage('The user has been successfully added.');
          }
        } else {
          setTitle('Success');
          setMessage('The user has been successfully added.');
        }
      } catch (error) {
        console.log('error: ', error);
      } finally {
        setAdding(false);
      }
    } else {
      setTitle('Invalid');
      setMessage('Client contact not active in Ultra Staff');
      setAdding(false);
    }
  };

  return (
    <div>
      <SubHeader
        buttons={[
          { icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigation('/dashboard') },
          { icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigation(-1) },
        ]}
        text="Admin Portal"
      />
      <div className="wrap">
        <div className="inner" style={{ width: isMobile ? '100%' : '70%' }}>
          <div className="bar">Add Client User</div>
          <div className="content">
            <Form>
              <Row>
                <Col>
                  <InputBox
                    loading={loading}
                    title="Client ID"
                    value={clientId}
                    span
                    onChange={e => setClientId(e.target.value)}
                    onBlur={fetchClient}
                    errorMessage="No client found"
                    error={nullResult || errors.includes('clientId')}
                  />
                </Col>
                <Col>
                  <Form.Label>Client Name</Form.Label>
                  <div className="label">{clientData.clientName}</div>
                </Col>
                <Col>
                  <Form.Label>Client Status</Form.Label>
                  <div className="label">{clientData.clientStatus}</div>
                </Col>
                <Col>
                  <Form.Label>AMC Code</Form.Label>
                  <div className="label">{clientData?.clientAccountMasterCode !== " " ? clientData?.clientAccountMasterCode : 'None'}</div>
                </Col>
              </Row>
              <div className="box">
                <Row>
                  <Col>
                    <Form.Label>Existing Contacts</Form.Label>
                    <Dropdown
                      onSelect={eventKey => {
                        setSelectedContact(clientData?.clientContacts[parseInt(eventKey)]);
                        setErrors([]);
                      }}>
                      <Dropdown.Toggle style={styles.dropdownStyle}>
                        {!selectedContact ? 'Select...' : `${selectedContact.firstName} ${selectedContact.lastName}`}
                      </Dropdown.Toggle>
                      {clientData?.clientContacts?.length ? (
                        <Dropdown.Menu>
                          {clientData?.clientContacts?.map((item, i) => (
                            <Dropdown.Item eventKey={`${i}`} key={item.lastName}>
                              {`${item.firstName} ${item.lastName}`}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      ) : null}
                    </Dropdown>
                    {errors.includes('existing') && <div className="error">choose contact</div>}
                  </Col>

                  <Col>
                    {selectedContact && (
                      <>
                        <InputBox disabled title="First Name" value={selectedContact?.firstName || ''} />
                        <InputBox
                          disabled
                          style={styles.marginTop}
                          title="Email"
                          value={selectedContact?.emailAddress || ''}
                        />
                        <InputBox
                          disabled
                          style={styles.marginTop}
                          title="Status"
                          value={selectedContact?.status || ''}
                        />
                      </>
                    )}
                  </Col>
                  <Col>
                    {selectedContact && (
                      <>
                        <InputBox disabled title="Last Name" value={selectedContact?.lastName || ''} />
                        <InputBox
                          disabled
                          style={styles.marginTop}
                          title="Cell Phone"
                          value={selectedContact?.smsOptIn === true ? selectedContact?.cellPhone : ''}
                          errorMessage="Cell Phone Number not found"
                          error={!selectedContact?.cellPhone}
                        />

                        <InputBox
                          disabled
                          style={styles.marginTop}
                          title="SMS Opt-in"
                          value={selectedContact?.smsOptIn ? 'Yes' : 'No'}
                          errorMessage="SMS Opt In Not Set"
                          error={typeof selectedContact?.smsOptIn !== 'boolean'}
                        />
                      </>
                    )}
                  </Col>
                </Row>
                {selectedContact && (
                  <>
                    <p>Select Clients by:</p>
                    <Row>
                      <span className="radio-group mr-4 mb-2">
                        <Radio
                          style={{
                            color: '#da0f2e',
                            transform: 'scale(1)',
                          }}
                          disabled={clientData?.clientAccountMasterCode === " "}
                          onChange={() => { handleSelectClientToggle(clientData?.clientName) }}
                          checked={selectedClient === clientData?.clientName}
                        />
                        <span>AMC</span>
                      </span>
                      <span className="radio-group mr-4">
                        <Radio
                          style={{
                            color: '#da0f2e',
                            transform: 'scale(1)',
                          }}
                          disabled={clientData?.clientAccountMasterCode === " "}
                          onChange={() => { handleSelectClientToggle('Single Client') }}
                          checked={selectedClient === 'Single Client'}
                        />
                        <span>Single Client</span>
                      </span>
                    </Row>
                  </>
                )}
                {selectedContact && (
                  <Row>
                    <Col />
                    <Col>
                      <div style={styles.center}>
                        <Button variant="primary" className="w-100" style={{ marginTop: 40, marginBottom: 20 }} onPress={add} text={adding ? 'Adding...' : 'Add'} disabled={adding} />
                      </div>
                    </Col>
                    <Col>
                      <div style={{ display: 'flex', height: '100%', justifyContent: 'flex-end' }}>
                        <Tooltip title="Refresh data from Ultra Staff" arrow>
                          <Button variant="secondary" style={{ height: 40, alignSelf: 'flex-end', marginBottom: 10 }} onPress={fetchClient} icon={<FontAwesomeIcon icon={faRedo} className="fa-lg" />} />
                        </Tooltip>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Form>
          </div>
        </div>
      </div>
      <ModalConfirmation
        title={title}
        body={message}
        primaryButtonTitle="Add Another"
        secondaryButtonTitle="Close"
        isOpen={title.length > 0}
        onClick={() => {
          setClientId('');
          setTitle('');
        }}
        secondaryOnClick={() => {
          if (title === 'Invalid') {
            setTitle('');
            setMessage('');
          } else {
            navigation(-1);
          }
        }}
        optionalClose
      />
      <Footer />
    </div>
  );
};

export default AddClientUser;
